<script setup lang="ts">
import {computed, defineProps, ref} from "vue";
import {usePlanetStore} from "@/stores/planetStore";
import PlanetProduction from "@/components/Sections/Planets/PlanetTabs/PlanetProduction.vue";
import PlanetBuildings from "@/components/Sections/Planets/PlanetTabs/PlanetBuildings.vue";
import PlanetDefence from "@/components/Sections/Planets/PlanetTabs/PlanetDefence.vue";
import PlanetTerraformer from '@/components/Sections/Planets/PlanetTabs/PlanetTerraformer.vue'
import PlanetDysonSwarm from '@/components/Sections/Planets/PlanetTabs/PlanetDysonSwarm.vue'
import PlanetStats from '@/components/Sections/Planets/PlanetTabs/PlanetStats.vue'
import PlanetAbandon from '@/components/Sections/Planets/PlanetTabs/PlanetAbandon.vue'
import PlanetPopulation from '@/components/Sections/Planets/PlanetTabs/PlanetPopulation.vue'
import PlanetRingworld from '@/components/Sections/Planets/PlanetTabs/PlanetRingworld.vue'
import { useGameStore } from '@/stores/gameStore'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores / models
 */
const planetStore = usePlanetStore()
const gameStore = useGameStore()

/**
 * Variables
 */
const currentTab = ref('production')

/**
 * Computed
 */
const planetData = computed(() => planetStore.findPlanetById(props.planetId)!)

const isRingworld = computed(() => Number(planetData.value.type) === 8)

const isParadise = computed(() => Number(planetData.value.type) === gameStore.player!.homeWorldPlanetType)

</script>

<template>
  <div class="text-sm font-medium text-center border-b text-metal-400 border-metal-700">
    <div class="overflow-x-auto whitespace-nowrap no-scrollbar">
      <ul class="flex">
        <li class="me-2">
          <a href="#"
             :class="{
                 'active-blue': currentTab === 'stats',
                 'text-blue-500 border-blue-500': currentTab === 'stats',
                 'text-metal-500 border-transparent': currentTab !== 'stats',
                 'inline-block p-4 border-b-2 rounded-t-lg hover:border-metal-300 hover:text-metal-300': true
             }"
             @click="currentTab = 'stats'">
            {{ t('planets.tabs.stats') }}
          </a>
        </li>
        <li class="me-2">
          <a href="#"
             :class="{
                 'active-blue': currentTab === 'production',
                 'text-blue-500 border-blue-500': currentTab === 'production',
                 'text-metal-500 border-transparent': currentTab !== 'production',
                 'inline-block p-4 border-b-2 rounded-t-lg hover:border-metal-300 hover:text-metal-300': true
             }"
             @click="currentTab = 'production'">
            {{ t('planets.tabs.production') }}
          </a>
        </li>
        <li class="me-2">
          <a href="#"
             :class="{
                 'active-blue': currentTab === 'buildings',
                 'text-blue-500 border-blue-500': currentTab === 'buildings',
                 'text-metal-500 border-transparent': currentTab !== 'buildings',
                 'inline-block p-4 border-b-2 rounded-t-lg  hover:border-metal-300 hover:text-metal-300': true
             }"
             @click="currentTab = 'buildings'">
            {{ t('planets.tabs.buildings') }}
          </a>
        </li>
        <li class="me-2">
          <a href="#"
             :class="{
                 'active-blue': currentTab === 'population',
                 'text-blue-500 border-blue-500': currentTab === 'population',
                 'text-metal-500 border-transparent': currentTab !== 'population',
                 'inline-block p-4 border-b-2 rounded-t-lg  hover:border-metal-300 hover:text-metal-300': true
             }"
             @click="currentTab = 'population'">
            {{ t('planets.tabs.population') }}
          </a>
        </li>
        <li class="me-2" v-if="isParadise">
          <a href="#"
             :class="{
                 'active-blue': currentTab === 'ringworld',
                 'text-blue-500 border-blue-500': currentTab === 'ringworld',
                 'text-metal-500 border-transparent': currentTab !== 'ringworld',
                 'inline-block p-4 border-b-2 rounded-t-lg hover:border-metal-300 hover:text-metal-300': true
             }"
             @click="currentTab = 'ringworld'">
            {{ t('planets.tabs.ringworld') }}
          </a>
        </li>
        <li class="me-2">
          <a href="#"
             :class="{
                 'active-blue': currentTab === 'defence',
                 'text-blue-500 border-blue-500': currentTab === 'defence',
                 'text-metal-500 border-transparent': currentTab !== 'defence',
                 'inline-block p-4 border-b-2 rounded-t-lg hover:border-metal-300 hover:text-metal-300': true
             }"
             @click="currentTab = 'defence'">
            {{ t('planets.tabs.defense') }}
          </a>
        </li>
        <li class="me-2" v-if="!isRingworld && !isParadise">
          <a href="#"
             :class="{
                 'active-blue': currentTab === 'terraformer',
                 'text-blue-500 border-blue-500': currentTab === 'terraformer',
                 'text-metal-500 border-transparent': currentTab !== 'terraformer',
                 'inline-block p-4 border-b-2 rounded-t-lg hover:border-metal-300 hover:text-metal-300': true
             }"
             @click="currentTab = 'terraformer'">
            {{ t('planets.tabs.terraformer') }}
          </a>
        </li>
        <li class="me-2">
          <a href="#"
             :class="{
                 'active-blue': currentTab === 'dysonswarm',
                 'text-blue-500 border-blue-500': currentTab === 'dysonswarm',
                 'text-metal-500 border-transparent': currentTab !== 'dysonswarm',
                 'inline-block p-4 border-b-2 rounded-t-lg hover:border-metal-300 hover:text-metal-300': true
             }"
             @click="currentTab = 'dysonswarm'">
            {{ t('planets.tabs.dysonSwarm') }}
          </a>
        </li>
        <li class="me-2">
          <a href="#"
             :class="{
                 'active-red': currentTab === 'abandon',
                 'text-red-500 border-red-500': currentTab === 'abandon',
                 'text-metal-500 border-transparent': currentTab !== 'abandon',
                 'inline-block p-4 border-b-2 rounded-t-lg hover:border-metal-300 hover:text-metal-300': true
             }"
             @click="currentTab = 'abandon'">
            {{ t('planets.tabs.abandon') }}
          </a>
        </li>
      </ul>
    </div>
  </div>
  <div v-if="currentTab === 'stats'">
    <PlanetStats :planet-id="planetData.id"></PlanetStats>
  </div>
  <div v-if="currentTab === 'production'">
    <PlanetProduction :planet-id="planetData?.id"></PlanetProduction>
  </div>
  <div v-else-if="currentTab === 'buildings'">
    <PlanetBuildings :planet-id="planetData.id"></PlanetBuildings>
  </div>
  <div v-else-if="currentTab === 'population'">
    <PlanetPopulation :planet-id="planetData.id"></PlanetPopulation>
  </div>
  <div v-else-if="currentTab === 'ringworld' && isParadise">
    <PlanetRingworld :planet-id="planetData.id"></PlanetRingworld>
  </div>
  <div v-else-if="currentTab === 'defence'">
    <PlanetDefence :planet-id="planetData.id"></PlanetDefence>
  </div>
  <div v-else-if="currentTab === 'terraformer' && !isRingworld && !isParadise">
    <PlanetTerraformer :planet-id="planetData.id"></PlanetTerraformer>
  </div>
  <div v-else-if="currentTab === 'dysonswarm'">
    <PlanetDysonSwarm :planet-id="planetData.id"></PlanetDysonSwarm>
  </div>
  <div v-else-if="currentTab === 'abandon'">
    <PlanetAbandon :planet-id="planetData.id"></PlanetAbandon>
  </div>
</template>

<style scoped lang="scss">
.nav-item {
  cursor: pointer;
}

.active-blue {
  background: linear-gradient(
    to top,
    rgba(59, 130, 246, 0.3) 0%,
    rgba(59, 130, 246, 0) 70%
  );
}

.active-red {
  background: linear-gradient(
    to top,
    rgba(239, 68, 68, 0.3) 0%,
    rgba(239, 68, 68, 0) 70%
  );
}
</style>
